/**
 * Credits API - 与 BO 交互的 API 层
 */

import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

import type {
    GetCreditsSummaryResponse,
    GetUsageRecordsParams,
    GetUsageRecordsResponse,
    GetCreditChangesParams,
    GetCreditChangesResponse,
    GetEntitlementContextResponse,
    GetWalletStatusResponse,
    GetUpgradeConfirmParams,
    GetUpgradeConfirmResponse,
    ExecuteUpgradeParams,
    ExecuteUpgradeResponse,
    GetDowngradeConfirmParams,
    GetDowngradeConfirmResponse,
    ExecuteDowngradeParams,
    ExecuteDowngradeResponse,
    CancelDowngradeResponse,
    ClaimFreeCreditsResponse,
    PersonalCreditsDetailResponse,
    CompanyCreditsDetailResponse,
    ConsumeRecordsParams,
    ConsumeRecordsResponse,
    RechargeRecordsResponse,
    GetRechargeEligibilityResponse,
    ActivityRechargeParams,
    ActivityRechargeResponse,
} from '../types';

export interface SwitchTenantParams {
    client_id: string;
    from: string;
    individual: boolean;
    individual_action_type?: string;
    tenant_id?: string;
}

export interface SwitchTenantResponse {
    token: string;
    refreshToken: string;
    replayUrl: string | null;
    errcode: string | null;
}

function toSwitchTenantFormData(data: SwitchTenantParams | string): string {
    if (typeof data === 'string') {
        return data;
    }
    const form = new URLSearchParams();
    form.append('client_id', data.client_id);
    form.append('from', data.from);
    form.append('individual', String(data.individual));
    if (data.individual_action_type) {
        form.append('individual_action_type', data.individual_action_type);
    }
    if (data.tenant_id) {
        form.append('tenant_id', data.tenant_id);
    }
    return form.toString();
}

export interface CreditsApi {
    getCreditsSummary(): Promise<GetCreditsSummaryResponse>;
    getUsageRecords(params?: GetUsageRecordsParams): Promise<GetUsageRecordsResponse>;
    getCreditChanges(params?: GetCreditChangesParams): Promise<GetCreditChangesResponse>;
    getEntitlementContext(): Promise<GetEntitlementContextResponse>;
    getWalletStatus(): Promise<GetWalletStatusResponse>;
    getUpgradeConfirmInfo(params: GetUpgradeConfirmParams): Promise<GetUpgradeConfirmResponse>;
    executeUpgrade(data: ExecuteUpgradeParams): Promise<ExecuteUpgradeResponse>;
    getDowngradeConfirmInfo(params: GetDowngradeConfirmParams): Promise<GetDowngradeConfirmResponse>;
    executeDowngrade(data: ExecuteDowngradeParams): Promise<ExecuteDowngradeResponse>;
    cancelDowngrade(): Promise<CancelDowngradeResponse>;
    claimFreeCredits(): Promise<ClaimFreeCreditsResponse>;
    checkFreeCreditsEligibility(): Promise<{ eligible: boolean; nextClaimDate?: string }>;
    getPersonalCreditsDetail(_tenantUserId: string): Promise<PersonalCreditsDetailResponse>;
    getPersonalRechargeRecords(): Promise<PersonalCreditsDetailResponse>;
    getCompanyCreditsDetail(_tenantUserId: string, _companyId: string): Promise<CompanyCreditsDetailResponse>;
    getCompanyRechargeRecords(_companyId: string): Promise<RechargeRecordsResponse>;
    getConsumeRecords(params?: ConsumeRecordsParams): Promise<ConsumeRecordsResponse>;
    /** 检查积分充值资格 */
    getRechargeEligibility(): Promise<GetRechargeEligibilityResponse>;
    /** 活动积分充值 */
    rechargeActivityCredits(params: ActivityRechargeParams): Promise<ActivityRechargeResponse>;
    /** 切换租户（Passport /switch-tenant） */
    switchTenant(data: SwitchTenantParams | string): Promise<SwitchTenantResponse>;
}

export const CreditsApi = Symbol('CreditsApi');

@injectable()
export class CreditsApiImpl extends PsHttpService implements CreditsApi {
    private get baseURL() {
        return `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/eureka/credits`;
    }

    get options() {
        return {
            baseURL: this.baseURL,
        };
    }

    /**
     * 获取 Credits Summary
     */
    async getCreditsSummary(): Promise<GetCreditsSummaryResponse> {
        return this.get<PatSnapCommonResponseData<GetCreditsSummaryResponse>>({
            url: `${this.baseURL}/summary`,
        }).then((res) => res.data as GetCreditsSummaryResponse);
    }

    /**
     * 获取 Usage Records
     */
    async getUsageRecords(params?: GetUsageRecordsParams): Promise<GetUsageRecordsResponse> {
        return this.get<PatSnapCommonResponseData<GetUsageRecordsResponse>>({
            url: `${this.baseURL}/usage-records`,
            params,
        }).then((res) => res.data as GetUsageRecordsResponse);
    }

    /**
     * 获取 Credit Changes
     */
    async getCreditChanges(params?: GetCreditChangesParams): Promise<GetCreditChangesResponse> {
        return this.get<PatSnapCommonResponseData<GetCreditChangesResponse>>({
            url: `${this.baseURL}/credit-changes`,
            params,
        }).then((res) => res.data as GetCreditChangesResponse);
    }

    /**
     * 获取权益上下文
     */
    async getEntitlementContext(): Promise<GetEntitlementContextResponse> {
        return this.get<PatSnapCommonResponseData<GetEntitlementContextResponse>>({
            url: `${this.baseURL}/entitlement-context`,
        }).then((res) => res.data as GetEntitlementContextResponse);
    }

    /**
     * 获取钱包状态
     */
    async getWalletStatus(): Promise<GetWalletStatusResponse> {
        return this.get<PatSnapCommonResponseData<GetWalletStatusResponse>>({
            url: `${this.baseURL}/wallet-status`,
        }).then((res) => res.data as GetWalletStatusResponse);
    }

    /**
     * 获取升级确认信息
     */
    async getUpgradeConfirmInfo(params: GetUpgradeConfirmParams): Promise<GetUpgradeConfirmResponse> {
        return this.get<PatSnapCommonResponseData<GetUpgradeConfirmResponse>>({
            url: `${this.baseURL}/upgrade/confirm`,
            params,
        }).then((res) => res.data as GetUpgradeConfirmResponse);
    }

    /**
     * 执行升级
     */
    async executeUpgrade(data: ExecuteUpgradeParams): Promise<ExecuteUpgradeResponse> {
        return this.post<PatSnapCommonResponseData<ExecuteUpgradeResponse>>({
            url: `${this.baseURL}/upgrade/execute`,
            data,
        }).then((res) => res.data as ExecuteUpgradeResponse);
    }

    /**
     * 获取降级确认信息
     */
    async getDowngradeConfirmInfo(params: GetDowngradeConfirmParams): Promise<GetDowngradeConfirmResponse> {
        return this.get<PatSnapCommonResponseData<GetDowngradeConfirmResponse>>({
            url: `${this.baseURL}/downgrade/confirm`,
            params,
        }).then((res) => res.data as GetDowngradeConfirmResponse);
    }

    /**
     * 执行降级预约
     */
    async executeDowngrade(data: ExecuteDowngradeParams): Promise<ExecuteDowngradeResponse> {
        return this.post<PatSnapCommonResponseData<ExecuteDowngradeResponse>>({
            url: `${this.baseURL}/downgrade/execute`,
            data,
        }).then((res) => res.data as ExecuteDowngradeResponse);
    }

    /**
     * 取消降级预约
     */
    async cancelDowngrade(): Promise<CancelDowngradeResponse> {
        return this.post<PatSnapCommonResponseData<CancelDowngradeResponse>>({
            url: `${this.baseURL}/downgrade/cancel`,
        }).then((res) => res.data as CancelDowngradeResponse);
    }

    /**
     * 领取免费 Credits
     */
    async claimFreeCredits(): Promise<ClaimFreeCreditsResponse> {
        return this.post<PatSnapCommonResponseData<ClaimFreeCreditsResponse>>({
            url: `${this.baseURL}/free-credits/claim`,
        }).then((res) => res.data as ClaimFreeCreditsResponse);
    }

    /**
     * 检查是否可以领取免费 Credits
     */
    async checkFreeCreditsEligibility(): Promise<{ eligible: boolean; nextClaimDate?: string }> {
        return this.get<PatSnapCommonResponseData<{ eligible: boolean; nextClaimDate?: string }>>({
            url: `${this.baseURL}/free-credits/eligibility`,
        }).then((res) => res.data as { eligible: boolean; nextClaimDate?: string });
    }

    /**
     * 查询个人积分详情
     * GET /api/eureka/credits/personal/detail
     */
    async getPersonalCreditsDetail(): Promise<PersonalCreditsDetailResponse> {
        return this.get<PatSnapCommonResponseData<PersonalCreditsDetailResponse>>({
            url: `${this.baseURL}/personal/detail`,
        }).then((res) => res.data as PersonalCreditsDetailResponse);
    }

    /**
     * 查询个人充值明细
     * GET /api/eureka/credits/personal/recharge-records
     */
    async getPersonalRechargeRecords(): Promise<PersonalCreditsDetailResponse> {
        return this.get<PatSnapCommonResponseData<PersonalCreditsDetailResponse>>({
            url: `${this.baseURL}/personal/recharge-records`,
        }).then((res) => res.data as PersonalCreditsDetailResponse);
    }

    /**
     * 查询用户可用公司积分详情
     * GET /api/eureka/credits/company/detail
     */
    async getCompanyCreditsDetail(): Promise<CompanyCreditsDetailResponse> {
        return this.get<PatSnapCommonResponseData<CompanyCreditsDetailResponse>>({
            url: `${this.baseURL}/company/detail`,
        }).then((res) => res.data as CompanyCreditsDetailResponse);
    }

    /**
     * 查询公司充值明细
     * GET /api/eureka/credits/company/recharge-records
     */
    async getCompanyRechargeRecords(_companyId: string): Promise<RechargeRecordsResponse> {
        return this.get<PatSnapCommonResponseData<RechargeRecordsResponse>>({
            url: `${this.baseURL}/company/recharge-records`,
            headers: {
                'x-tenant-id': _companyId,
            },
        }).then((res) => res.data as RechargeRecordsResponse);
    }

    /**
     * 查询积分消耗明细（游标分页）
     * POST /api/eureka/credits/consume-records
     */
    async getConsumeRecords(params?: ConsumeRecordsParams): Promise<ConsumeRecordsResponse> {
        return this.post<PatSnapCommonResponseData<ConsumeRecordsResponse>>({
            url: `${this.baseURL}/consume-records`,
            data: {
                page_no: params?.page_no || 1,
                consume_at_start: params?.consumeAtStart,
                consume_at_end: params?.consumeAtEnd,
                cursor_created_at: params?.cursorCreatedAt ?? null,
                cursor_id: params?.cursorId || '',
                page_size: params?.pageSize || 20,
                status: params?.status || 'success',
            },
        }).then((res) => {
            const raw = res.data as any;
            return {
                items: (raw.items || []).map((item: any) => ({
                    id: item.record_id,
                    businessId: item.business_id,
                    businessTitle: item.business_title,
                    sourceId: item.source_id,
                    description: item.description,
                    source: item.source,
                    rawType: item.raw_type,
                    remark: item.remark,
                    validBeginAt: item.valid_begin_at,
                    validEndAt: item.valid_end_at,
                    balanceBefore: item.balance_before,
                    metadata: item.metadata,
                    tenantUserId: item.metadata?.tenant_user_id || '',
                    companyId: item.metadata?.company_id || '',
                    platform: item.metadata?.platform || 'web',
                    productId: item.metadata?.product_id || '',
                    deductType: item.source || '',
                    featureId: item.source_id || '',
                    deductCredits: item.amount || 0,
                    quantity: item.metadata?.quantity || 0,
                    unit: item.metadata?.unit || '',
                    status: item.status || '',
                    creditsType: item.metadata?.credits_type || '',
                    unitPrice: item.metadata?.unit_price || 0,
                    consumeAt: item.occurred_at || 0,
                    createdAt: item.created_at || 0,
                    balanceBeforeDeduct: item.balance_before || 0,
                    mcpServerId: item.metadata?.mcp_server_id || undefined,
                    mcpToolId: item.metadata?.mcp_tool_id || undefined,
                    llmModel: item.metadata?.llm_model || undefined,
                })),
                totalSize: raw.pagination?.total_size || 0,
                pageSize: raw.pagination?.page_size || 20,
                nextCursorCreatedAt: raw.pagination?.next_cursor_created_at,
                nextCursorId: raw.pagination?.next_cursor_id,
            } as ConsumeRecordsResponse;
        });
    }

    /**
     * 检查积分充值资格
     * GET /api/eureka/credits/personal/recharge-info
     */
    async getRechargeEligibility(): Promise<GetRechargeEligibilityResponse> {
        const res = await this.get<any>({
            url: `${this.baseURL}/personal/recharge-info`,
        });
        return res as GetRechargeEligibilityResponse;
    }

    /**
     * 活动积分充值
     * POST /api/eureka/credits/activity/recharge
     */
    async rechargeActivityCredits(params: ActivityRechargeParams): Promise<ActivityRechargeResponse> {
        const res = await this.post<any>({
            url: `${this.baseURL}/activity/recharge`,
            data: params,
        });
        return res as ActivityRechargeResponse;
    }

    /**
     * 切换租户
     * POST {passport}/switch-tenant
     * 认证依赖 Passport Session Cookie，需 withCredentials
     */
    async switchTenant(data: SwitchTenantParams | string): Promise<SwitchTenantResponse> {
        const res = await this.httpClient<SwitchTenantResponse>({
            url: `${import.meta.env.VITE_PASSPORT_URI}/switch-tenant`,
            method: 'POST',
            withCredentials: true,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            data: toSwitchTenantFormData(data),
        });
        return res.data;
    }
}
